import {createSearchParams, useNavigate, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {FunctionalDiagramContext, SET_FUNCTIONAL_DIAGRAM_LIST,} from './context/context'
import InputText from '../../common/InputText'
import FunctionalDiagramTable from './FunctionalDiagramTable'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {encrypt} from '../../../utils/encryption'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import {useIntl} from 'react-intl'
import ExportMissingConnectors from './export_missing_connectors'
import UnMappedSubSystem from './unmapped_sub_system'
import { notifyError } from '../../../utils/notifications'

const FunctionalDiagramList = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { vehicle } = useParams()

  const [state, dispatch] = useContext(FunctionalDiagramContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(
    `/api/sub_system/?search=${search}&is_connector_search=true&make=${vehicle}`
  )

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const buildUrl = (search) => {
    const params = new URLSearchParams({
      search,
      is_connector_search: true,
      make: vehicle,
    })
    return `/api/sub_system/?${params.toString()}`
  }

  const getFunctionalDiagramList = useCallback(
    async (url) => {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({
            id: 'functionalDiagram.fetchingData',
          }),
          showLoader: true,
        },
        notification: {},
      })

      currentUrl.current = url ? url : currentUrl.current
      let res = await getRequest(currentUrl.current)
      setPagination(res.pagination)
      dispatch({ type: SET_FUNCTIONAL_DIAGRAM_LIST, data: res.results })

      navDispatch({
        type: 'loader',
        data: { showLoader: false },
        notification: {},
      })

      // After the loader dispatch above: a 'loader' action overwrites `notification`,
      // so notifying before it would be wiped out in the same React batch.
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.loadError' }))
      }
    },
    [intl, navDispatch]
  )

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.functionalDiagram' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getFunctionalDiagramList()
  }, [])

  useEffect(() => {
    currentUrl.current = buildUrl(search)
    getFunctionalDiagramList()
  }, [debouncedSearch, getFunctionalDiagramList])

  const onEdit = useCallback(
    (functional_diagram) => {
      let enc = encrypt(String(functional_diagram.id))
      navigate(`/vehicles/${vehicle}/functional-diagram/${enc}`)
    },
    [vehicle, navigate]
  )

  const deleteFunctionalDiagram = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'functionalDiagram.deleting' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await deleteRequest(
      `/api/sub_system/functional-diagram/delete/${vehicle}/${record.id}/`
    )
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    if (res?.status !== false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'functionalDiagram.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getFunctionalDiagramList()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'functionalDiagram.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = useCallback(
    (record) => {
      modal.confirm({
        title: intl.formatMessage({ id: 'functionalDiagram.confirmDelete' }),
        icon: <ExclamationCircleOutlined />,
        content: intl.formatMessage(
          { id: 'functionalDiagram.confirmDeleteContent' },
          { name: record.name }
        ),
        onOk: () => deleteFunctionalDiagram(record),
        centered: true,
        maskClosable: true,
      })
    },
    [modal, intl, vehicle]
  )

  const dataSource = useMemo(() => {
    if (!state?.list?.length) return []
    return state.list.map((item, index) => ({
      ...item,
      key: `functional-diagram-${index}`,
      rowKey: `functional-diagram-${item.id}`,
      index: index,
    }))
  }, [state.list])

  return (
    <>
      {contextHolder}
      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex
              gap={16}
              wrap={false}
              className="full-width"
              align="center"
            >
              <InputText
                name="search_functional_diagram"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'functionalDiagram.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-functional-diagram"
                onChange={(value) => {
                  setSearch(value)
                }}
              />
              
              <Space
                size="middle"
                align="center"
                style={{ marginLeft: 'auto' }}
              >
                <ExportMissingConnectors />
                <UnMappedSubSystem />
              </Space>
            </Flex>
          </Form>
          
          <FunctionalDiagramTable
            dataSource={dataSource}
            pagination={pagination}
            getFunctionalDiagramList={getFunctionalDiagramList}
            onEdit={onEdit}
            onDelete={confirmDelete}
          />
        </Row>
      </div>
    </>
  )
}

export default FunctionalDiagramList
